import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, UserPlus, Network, TrendingUp, Award, Building, Eye, Trash2, Check, Copy, Search, Edit2, Save, Settings as SettingsIcon, IndianRupee, Percent, Wallet, CheckCircle, XCircle, Key, AlertTriangle, Ban, FileText, Calendar, User, ChevronDown, ChevronRight } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';
import MLMTreeNode from '@/components/MLMTreeNode';

const getRankColor = rank => ({ 'Bronze': 'bg-amber-700 text-white', 'Silver': 'bg-slate-400 text-white', 'Gold': 'bg-yellow-500 text-white', 'Platinum': 'bg-slate-600 text-white', 'Diamond': 'bg-cyan-500 text-white' }[rank] || 'bg-slate-500 text-white');
const getStatusColor = status => ({ 'active': 'bg-green-100 text-green-800', 'pending': 'bg-yellow-100 text-yellow-800', 'suspended': 'bg-red-100 text-red-800' }[status] || 'bg-slate-100 text-slate-800');

export default function PartnerNetwork({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [agents, setAgents] = useState([]);
  const [properties, setProperties] = useState([]);
  const [settings, setSettings] = useState(null);
  const [addAgentOpen, setAddAgentOpen] = useState(false);
  const [viewAgentOpen, setViewAgentOpen] = useState(false);
  const [editAgentOpen, setEditAgentOpen] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [agentForm, setAgentForm] = useState({ name: '', email: '', phone: '', password: '', sponsor_code: '' });
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '', status: '', rank: '' });
  const [newPassword, setNewPassword] = useState('');
  const [editingSettings, setEditingSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState(null);
  const [propertyCommissionOpen, setPropertyCommissionOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [commissionForm, setCommissionForm] = useState({ commission_type: 'percentage', commission_value: 2, level_distribution: {} });
  const [teamTree, setTeamTree] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [showInactiveOnly, setShowInactiveOnly] = useState(false);
  const [mlmBookings, setMlmBookings] = useState([]);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [selectedAgentIds, setSelectedAgentIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  
  // Payment & Registry Dialog States
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [registryDialogOpen, setRegistryDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ amount: '', payment_mode: 'Cash', remarks: '' });
  const [registryForm, setRegistryForm] = useState({ registry_number: '', registry_date: '' });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, agentsRes, propsRes, settingsRes] = await Promise.all([
        api.get('/mlm/dashboard-stats'), api.get('/mlm/agents'), api.get('/mlm/properties'), api.get('/mlm/settings')
      ]);
      setStats(statsRes.data); setAgents(agentsRes.data); setProperties(propsRes.data); setSettings(settingsRes.data); setSettingsForm(settingsRes.data);
    } catch (error) { console.error(error); toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const fetchTeamTree = async () => { try { const r = await api.get('/mlm/team-tree'); setTeamTree(r.data); } catch (e) { console.error(e); } };
  const fetchWithdrawals = async () => { try { const r = await api.get('/mlm/admin/withdrawals'); setWithdrawals(r.data); } catch (e) { console.error(e); } };
  const fetchMlmBookings = async () => { try { const r = await api.get('/mlm/bookings'); setMlmBookings(r.data); } catch (e) { console.error(e); } };

  const toggleNodeExpand = (nodeId) => {
    setExpandedNodes(prev => ({ ...prev, [nodeId]: !prev[nodeId] }));
  };

  const handleBookingStatusChange = async (bookingId, newStatus) => {
    const remarks = newStatus === 'rejected' ? window.prompt('Rejection reason:') : '';
    try {
      await api.put('/mlm/bookings/' + bookingId + '/status', { status: newStatus, remarks });
      toast.success('Booking ' + newStatus);
      fetchMlmBookings();
      fetchData();
    } catch (e) { toast.error('Failed'); }
  };

  const handleAddPayment = (booking) => {
    setSelectedBooking(booking);
    setPaymentForm({ amount: '', payment_mode: 'Cash', remarks: '' });
    setPaymentDialogOpen(true);
  };

  const handleSubmitPayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Please enter valid amount');
      return;
    }
    try {
      await api.put('/admin/bookings/' + selectedBooking.id + '/payment', { 
        action: 'add_payment', 
        amount: parseFloat(paymentForm.amount),
        payment_mode: paymentForm.payment_mode,
        remarks: paymentForm.remarks
      });
      toast.success('Payment recorded successfully!');
      setPaymentDialogOpen(false);
      fetchMlmBookings();
      fetchData();
    } catch (e) { 
      toast.error(e.response?.data?.detail || 'Failed to record payment'); 
    }
  };

  const handleCompleteRegistry = (booking) => {
    if (booking.payment_status !== 'paid') {
      toast.error('Payment must be complete before registry');
      return;
    }
    setSelectedBooking(booking);
    setRegistryForm({ registry_number: '', registry_date: new Date().toISOString().split('T')[0] });
    setRegistryDialogOpen(true);
  };

  const handleSubmitRegistry = async () => {
    if (!registryForm.registry_number) {
      toast.error('Please enter registry number');
      return;
    }
    try {
      await api.put('/admin/bookings/' + selectedBooking.id + '/registry', { 
        action: 'complete', 
        registry_number: registryForm.registry_number,
        registry_date: registryForm.registry_date
      });
      toast.success('Registry completed! Commission released to agents.');
      setRegistryDialogOpen(false);
      fetchMlmBookings();
      fetchData();
    } catch (e) { 
      toast.error(e.response?.data?.detail || 'Failed to complete registry'); 
    }
  };

  const handleMarkPaymentComplete = async (booking) => {
    if (!window.confirm(`Mark payment as complete for ${booking.customer_name}? This will make commission eligible.`)) return;
    try {
      await api.put('/admin/bookings/' + booking.id + '/payment', { action: 'mark_complete' });
      toast.success('Payment marked as complete!');
      fetchMlmBookings();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleProcessWithdrawal = async (id, action) => {
    const remarks = action === 'reject' ? window.prompt('Rejection reason:') : '';
    try { await api.put('/mlm/admin/withdrawals/' + id, { action, remarks }); toast.success('Done!'); fetchWithdrawals(); } catch (e) { toast.error('Failed'); }
  };

  const handleAddAgent = async e => {
    e.preventDefault();
    try { await api.post('/mlm/agents', agentForm); toast.success('Agent added!'); setAddAgentOpen(false); setAgentForm({ name: '', email: '', phone: '', password: '', sponsor_code: '' }); fetchData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const handleEditAgent = async e => {
    e.preventDefault();
    try { 
      await api.put('/mlm/agents/' + selectedAgent.id, editForm); 
      toast.success('Agent updated!'); 
      setEditAgentOpen(false); 
      fetchData(); 
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const handleResetPassword = async e => {
    e.preventDefault();
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    try {
      await api.post('/mlm/agents/' + selectedAgent.id + '/reset-password', { password: newPassword });
      toast.success('Password reset successfully!');
      setResetPasswordOpen(false);
      setNewPassword('');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const handleSuspendAgent = async (agentId) => {
    if (!window.confirm('Suspend this agent?')) return;
    try { await api.post('/mlm/agents/' + agentId + '/suspend'); toast.success('Agent suspended'); fetchData(); } catch (e) { toast.error('Failed'); }
  };

  const handleActivateAgent = async id => { try { await api.post('/mlm/agents/' + id + '/activate'); toast.success('Activated!'); fetchData(); } catch (e) { toast.error('Failed'); } };
  const handleDeleteAgent = async id => { if (!window.confirm('Delete?')) return; try { await api.delete('/mlm/agents/' + id); toast.success('Deleted!'); setSelectedAgentIds(prev => { const n = new Set(prev); n.delete(id); return n; }); fetchData(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } };

  const toggleAgentSelect = (id) => {
    setSelectedAgentIds(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const toggleSelectAllAgents = () => {
    if (selectedAgentIds.size === filteredAgents.length) {
      setSelectedAgentIds(new Set());
    } else {
      setSelectedAgentIds(new Set(filteredAgents.map(a => a.id)));
    }
  };

  const handleBulkDeleteAgents = async () => {
    if (selectedAgentIds.size === 0) return;
    if (!window.confirm(`Kya aap ${selectedAgentIds.size} agents delete karna chahte hain?`)) return;
    setBulkDeleting(true);
    try {
      const res = await api.post('/mlm/agents/bulk-delete', { ids: Array.from(selectedAgentIds) });
      toast.success(res.data.message);
      setSelectedAgentIds(new Set());
      fetchData();
    } catch (error) {
      toast.error('Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };

  const openEditDialog = (agent) => {
    setSelectedAgent(agent);
    setEditForm({ name: agent.name, email: agent.email, phone: agent.phone, status: agent.status, rank: agent.rank });
    setEditAgentOpen(true);
  };

  const openResetPasswordDialog = (agent) => {
    setSelectedAgent(agent);
    setNewPassword('');
    setResetPasswordOpen(true);
  };

  const handleToggleMLMProperty = async (id, enabled) => {
    if (enabled) { try { await api.post('/mlm/properties/' + id + '/disable'); toast.success('Disabled'); fetchData(); } catch (e) { toast.error('Failed'); } }
    else { const prop = properties.find(p => p.id === id); setSelectedProperty(prop); setCommissionForm({ commission_type: 'percentage', commission_value: 2, level_distribution: { "1": 40, "2": 20, "3": 15, "4": 10, "5": 5, "6": 3, "7": 3, "8": 2, "9": 1, "10": 1 } }); setPropertyCommissionOpen(true); }
  };

  const handleSavePropertyCommission = async () => {
    const total = Object.values(commissionForm.level_distribution).reduce((a, b) => Number(a) + Number(b), 0);
    if (Math.abs(total - 100) > 0.01) { toast.error('Sum must be 100%'); return; }
    try {
      if (selectedProperty.mlm_enabled) await api.put('/mlm/properties/' + selectedProperty.id + '/commission', commissionForm);
      else await api.post('/mlm/properties/' + selectedProperty.id + '/enable', commissionForm);
      toast.success('Saved!'); setPropertyCommissionOpen(false); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleSaveSettings = async () => {
    try { await api.put('/mlm/settings', settingsForm); toast.success('Saved!'); setEditingSettings(false); setSettings(settingsForm); } catch (e) { toast.error('Failed'); }
  };

  const updateLevelCommission = (level, value) => setSettingsForm(prev => ({ ...prev, level_commissions: { ...prev.level_commissions, [level]: parseFloat(value) || 0 } }));
  const updateDistribution = (level, value) => setCommissionForm(prev => ({ ...prev, level_distribution: { ...prev.level_distribution, [level]: parseFloat(value) || 0 } }));
  const copyReferralCode = code => { navigator.clipboard.writeText(code); toast.success('Copied!'); };
  
  // Filter agents
  let filteredAgents = agents.filter(a => (a.name?.toLowerCase().includes(searchQuery.toLowerCase())) || (a.email?.toLowerCase().includes(searchQuery.toLowerCase())) || (a.phone?.includes(searchQuery)) || (a.referral_code?.toLowerCase().includes(searchQuery.toLowerCase())));
  if (showInactiveOnly) filteredAgents = filteredAgents.filter(a => a.is_inactive);
  
  // Count inactive agents
  const inactiveCount = agents.filter(a => a.is_inactive).length;

  // Format last login
  const formatLastLogin = (lastLogin, daysInactive) => {
    if (!lastLogin) return 'Never';
    const date = new Date(lastLogin);
    if (daysInactive !== null && daysInactive !== undefined) {
      if (daysInactive === 0) return 'Today';
      if (daysInactive === 1) return 'Yesterday';
      return daysInactive + ' days ago';
    }
    return date.toLocaleDateString();
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="partner-network">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div><h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><Network className="text-emerald-600" />Ganesa Partner Network</h1><p className="text-slate-500 text-sm">Manage your MLM agents and team</p></div>
          <Button onClick={() => setAddAgentOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 gap-2"><UserPlus size={18} />Add Agent</Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-slate-100 p-1 rounded-xl">
            <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="agents">Agents {inactiveCount > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">{inactiveCount}</span>}</TabsTrigger>
            <TabsTrigger value="bookings" onClick={fetchMlmBookings}>Bookings</TabsTrigger>
            <TabsTrigger value="tree" onClick={fetchTeamTree}>Team Tree</TabsTrigger>
            <TabsTrigger value="withdrawals" onClick={fetchWithdrawals}>Withdrawals</TabsTrigger>
            <TabsTrigger value="properties">MLM Properties</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="space-y-6">
            {loading ? <div className="flex items-center justify-center py-12"><div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div></div> : stats && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white"><CardContent className="pt-6"><Users size={24} className="mb-2 opacity-80" /><p className="text-3xl font-bold">{stats.total_agents}</p><p className="text-emerald-100 text-sm">Total Agents</p></CardContent></Card>
                  <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white"><CardContent className="pt-6"><Check size={24} className="mb-2 opacity-80" /><p className="text-3xl font-bold">{stats.active_agents}</p><p className="text-blue-100 text-sm">Active Agents</p></CardContent></Card>
                  <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white"><CardContent className="pt-6"><Building size={24} className="mb-2 opacity-80" /><p className="text-3xl font-bold">{stats.total_bookings}</p><p className="text-amber-100 text-sm">Total Bookings</p></CardContent></Card>
                  <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white"><CardContent className="pt-6"><TrendingUp size={24} className="mb-2 opacity-80" /><p className="text-3xl font-bold">₹{((stats.total_sales || 0) / 100000).toFixed(1)}L</p><p className="text-purple-100 text-sm">Total Sales</p></CardContent></Card>
                </div>
                
                {/* Inactive Agents Alert */}
                {inactiveCount > 0 && (
                  <Card className="border-red-300 bg-red-50">
                    <CardContent className="pt-6 flex items-center gap-3">
                      <AlertTriangle className="text-red-600" size={24} />
                      <div>
                        <p className="font-semibold text-red-800">{inactiveCount} Inactive Agents</p>
                        <p className="text-sm text-red-600">These agents haven't logged in for more than 30 days</p>
                      </div>
                      <Button variant="outline" size="sm" className="ml-auto text-red-600 border-red-300" onClick={() => { setActiveTab('agents'); setShowInactiveOnly(true); }}>View All</Button>
                    </CardContent>
                  </Card>
                )}

                <Card><CardHeader><CardTitle className="text-lg flex items-center gap-2"><Award className="text-emerald-600" />Agents by Rank</CardTitle></CardHeader><CardContent><div className="grid grid-cols-5 gap-4">{['Bronze', 'Silver', 'Gold', 'Platinum', 'Diamond'].map(rank => (<div key={rank} className="text-center p-4 rounded-xl bg-slate-50"><Badge className={getRankColor(rank) + ' mb-2'}>{rank}</Badge><p className="text-2xl font-bold">{stats.rank_counts?.[rank] || 0}</p></div>))}</div></CardContent></Card>
                {stats.pending_agents > 0 && <Card className="border-amber-200 bg-amber-50"><CardContent className="pt-6"><p className="font-semibold text-amber-800">{stats.pending_agents} agents pending</p><Button size="sm" variant="outline" className="mt-2" onClick={() => setActiveTab('agents')}>Review</Button></CardContent></Card>}
              </div>
            )}
          </TabsContent>

          <TabsContent value="agents" className="space-y-4">
            <div className="flex gap-4 items-center">
              <div className="relative flex-1"><Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><Input placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" /></div>
              <Button variant={showInactiveOnly ? 'default' : 'outline'} size="sm" onClick={() => setShowInactiveOnly(!showInactiveOnly)} className={showInactiveOnly ? 'bg-red-600 hover:bg-red-700' : ''}>
                <AlertTriangle size={16} className="mr-2" />{showInactiveOnly ? 'Showing Inactive' : 'Show Inactive Only'}
              </Button>
            </div>
            <Card><CardContent className="pt-6">
              {/* Bulk Delete Toolbar */}
              {selectedAgentIds.size > 0 && (
                <div className="flex items-center justify-between mb-4 p-3 bg-red-50 border border-red-200 rounded-xl" data-testid="bulk-delete-agents-toolbar">
                  <span className="text-sm font-medium text-red-700">{selectedAgentIds.size} selected</span>
                  <Button size="sm" variant="destructive" data-testid="bulk-delete-agents-btn" onClick={handleBulkDeleteAgents} disabled={bulkDeleting} className="gap-2">
                    <Trash2 size={14} />{bulkDeleting ? 'Deleting...' : `Delete ${selectedAgentIds.size} Selected`}
                  </Button>
                </div>
              )}
              <Table><TableHeader><TableRow>
                <TableHead className="w-10">
                  <input type="checkbox" data-testid="select-all-agents" checked={filteredAgents.length > 0 && selectedAgentIds.size === filteredAgents.length} onChange={toggleSelectAllAgents} className="rounded border-slate-300 accent-emerald-600 w-4 h-4" />
                </TableHead>
                <TableHead>Agent</TableHead><TableHead>Contact</TableHead><TableHead>Code</TableHead><TableHead>Last Login</TableHead><TableHead>Rank</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {loading ? <TableRow><TableCell colSpan={8} className="text-center py-8">Loading...</TableCell></TableRow> : filteredAgents.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500">No agents</TableCell></TableRow> : filteredAgents.map(agent => (
                    <TableRow key={agent.id} className={`${agent.is_inactive ? 'bg-red-50' : ''} ${selectedAgentIds.has(agent.id) ? 'bg-emerald-50' : ''}`}>
                      <TableCell>
                        <input type="checkbox" data-testid={`select-agent-${agent.id}`} checked={selectedAgentIds.has(agent.id)} onChange={() => toggleAgentSelect(agent.id)} className="rounded border-slate-300 accent-emerald-600 w-4 h-4" />
                      </TableCell>
                      <TableCell><div className="flex items-center gap-3"><div className={'w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ' + (agent.is_inactive ? 'bg-red-400' : 'bg-gradient-to-br from-emerald-500 to-teal-600')}>{agent.name?.charAt(0)?.toUpperCase() || '?'}</div><div><p className="font-medium">{agent.name}</p><p className="text-xs text-slate-500">{new Date(agent.created_at).toLocaleDateString()}</p></div></div></TableCell>
                      <TableCell><p className="text-sm">{agent.email}</p><p className="text-xs text-slate-500">{agent.phone}</p></TableCell>
                      <TableCell><div className="flex items-center gap-2"><code className="bg-slate-100 px-2 py-1 rounded text-sm">{agent.referral_code}</code><Button size="sm" variant="ghost" onClick={() => copyReferralCode(agent.referral_code)}><Copy size={14} /></Button></div></TableCell>
                      <TableCell>
                        <div className={'text-sm ' + (agent.is_inactive ? 'text-red-600 font-semibold' : 'text-slate-600')}>
                          {formatLastLogin(agent.last_login, agent.days_since_login)}
                          {agent.is_inactive && <span className="block text-xs text-red-500 flex items-center gap-1"><AlertTriangle size={12} />Inactive</span>}
                        </div>
                      </TableCell>
                      <TableCell><Badge className={getRankColor(agent.rank)}>{agent.rank}</Badge></TableCell>
                      <TableCell><Badge className={getStatusColor(agent.status)}>{agent.status}</Badge></TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {agent.status === 'pending' && <Button size="sm" variant="outline" onClick={() => handleActivateAgent(agent.id)} className="text-green-600" title="Activate"><Check size={14} /></Button>}
                          {agent.status === 'active' && <Button size="sm" variant="outline" onClick={() => handleSuspendAgent(agent.id)} className="text-orange-600" title="Suspend"><Ban size={14} /></Button>}
                          {agent.status === 'suspended' && <Button size="sm" variant="outline" onClick={() => handleActivateAgent(agent.id)} className="text-green-600" title="Reactivate"><Check size={14} /></Button>}
                          <Button size="sm" variant="outline" onClick={() => { setSelectedAgent(agent); setViewAgentOpen(true); }} title="View"><Eye size={14} /></Button>
                          <Button size="sm" variant="outline" onClick={() => openEditDialog(agent)} className="text-blue-600" title="Edit"><Edit2 size={14} /></Button>
                          <Button size="sm" variant="outline" onClick={() => openResetPasswordDialog(agent)} className="text-purple-600" title="Reset Password"><Key size={14} /></Button>
                          <Button size="sm" variant="outline" onClick={() => handleDeleteAgent(agent.id)} className="text-red-600" title="Delete"><Trash2 size={14} /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>
          </TabsContent>

          <TabsContent value="bookings" className="space-y-4">
            <Card><CardHeader><CardTitle className="flex items-center gap-2"><FileText className="text-emerald-600" />Agent Bookings</CardTitle><p className="text-sm text-slate-500">Commission released only after Payment Complete + Registry Done</p></CardHeader><CardContent>
              <Table><TableHeader><TableRow><TableHead>Property</TableHead><TableHead>Customer</TableHead><TableHead>Agent</TableHead><TableHead>Payment</TableHead><TableHead>Status</TableHead><TableHead>Commission</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {mlmBookings.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">No agent bookings yet</TableCell></TableRow> : mlmBookings.map(b => (
                    <TableRow key={b.id} className={b.commission_status === 'released' ? 'bg-green-50' : ''}>
                      <TableCell>
                        <p className="font-medium">{b.property_name}</p>
                        <p className="text-xs text-slate-500">Total: ₹{(b.total_amount || 0).toLocaleString()}</p>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium">{b.customer_name}</p>
                        <p className="text-xs text-slate-500">{b.customer_mobile}</p>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium">{b.agent_name}</p>
                        <p className="text-xs text-slate-500">{b.agent_referral_code}</p>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <p className="text-sm">Paid: <span className="font-bold text-emerald-600">₹{(b.paid_amount || b.booking_amount || 0).toLocaleString()}</span></p>
                          <p className="text-xs text-slate-500">Balance: ₹{(b.balance_amount || 0).toLocaleString()}</p>
                          <Badge className={b.payment_status === 'paid' ? 'bg-green-100 text-green-800 text-xs' : 'bg-yellow-100 text-yellow-800 text-xs'}>
                            {b.payment_status || 'partial'}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <Badge className={
                            b.booking_status === 'completed' ? 'bg-green-100 text-green-800' :
                            b.booking_status === 'registry_done' ? 'bg-blue-100 text-blue-800' :
                            b.booking_status === 'payment_complete' ? 'bg-purple-100 text-purple-800' :
                            'bg-yellow-100 text-yellow-800'
                          }>
                            {b.booking_status || 'pending'}
                          </Badge>
                          {b.registry_status && (
                            <p className="text-xs text-slate-500">Registry: {b.registry_status}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          b.commission_status === 'released' ? 'bg-green-500 text-white' :
                          b.commission_status === 'eligible' ? 'bg-blue-100 text-blue-800' :
                          'bg-slate-100 text-slate-600'
                        }>
                          {b.commission_status || 'pending'}
                        </Badge>
                        {b.total_commission && (
                          <p className="text-xs font-semibold text-emerald-600 mt-1">₹{(b.total_commission || 0).toLocaleString()}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          {b.payment_status !== 'paid' && (
                            <>
                              <Button size="sm" variant="outline" className="text-purple-600 text-xs" onClick={() => handleAddPayment(b)}>
                                <IndianRupee size={12} className="mr-1" />Add Payment
                              </Button>
                              {b.balance_amount > 0 && b.paid_amount > 0 && (
                                <Button size="sm" variant="ghost" className="text-green-600 text-xs" onClick={() => handleMarkPaymentComplete(b)}>
                                  <CheckCircle size={12} className="mr-1" />Mark Paid
                                </Button>
                              )}
                            </>
                          )}
                          {b.payment_status === 'paid' && b.registry_status !== 'completed' && (
                            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white text-xs" onClick={() => handleCompleteRegistry(b)}>
                              <FileText size={12} className="mr-1" />Complete Registry
                            </Button>
                          )}
                          {b.commission_status === 'released' && (
                            <div className="flex items-center gap-1 text-green-600">
                              <CheckCircle size={14} />
                              <span className="text-xs font-medium">Done</span>
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>
          </TabsContent>

          <TabsContent value="tree" className="space-y-4">
            <Card><CardHeader><CardTitle className="flex items-center gap-2"><Network className="text-emerald-600" />Team Tree</CardTitle><p className="text-sm text-slate-500">Click on agents to expand/collapse their downline</p></CardHeader><CardContent>
              {teamTree.length === 0 ? (
                <div className="text-center py-12 text-slate-500"><Network size={48} className="mx-auto mb-4 opacity-50" /><p>No agents in the network</p></div>
              ) : (
                <div className="max-h-[600px] overflow-auto p-2">
                  {teamTree.map(node => <MLMTreeNode key={node.id} node={node} level={0} expandedNodes={expandedNodes} onToggle={toggleNodeExpand} />)}
                </div>
              )}
            </CardContent></Card>
          </TabsContent>

          <TabsContent value="withdrawals" className="space-y-4">
            <Card><CardHeader><CardTitle className="flex items-center gap-2"><Wallet className="text-emerald-600" />Withdrawal Requests</CardTitle></CardHeader><CardContent>
              <Table><TableHeader><TableRow><TableHead>Agent</TableHead><TableHead>Amount</TableHead><TableHead>Bank Details</TableHead><TableHead>Status</TableHead><TableHead>Date</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {withdrawals.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">No requests</TableCell></TableRow> : withdrawals.map(w => (
                    <TableRow key={w.id}>
                      <TableCell className="font-medium">{w.agent_name}</TableCell>
                      <TableCell className="font-bold text-emerald-600">₹{(w.amount || 0).toLocaleString()}</TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <p className="font-medium text-sm">{w.bank_name || 'N/A'}</p>
                          <p className="text-xs text-slate-600">A/C: {w.account_number || 'N/A'}</p>
                          <p className="text-xs text-slate-500">IFSC: <span className="font-mono font-semibold">{w.ifsc_code || 'N/A'}</span></p>
                          <p className="text-xs text-slate-400">{w.account_holder_name || ''}</p>
                        </div>
                      </TableCell>
                      <TableCell><Badge className={w.status === 'approved' ? 'bg-green-100 text-green-800' : w.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}>{w.status}</Badge></TableCell>
                      <TableCell className="text-sm text-slate-500">{new Date(w.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>{w.status === 'pending' && <div className="flex gap-2"><Button size="sm" variant="outline" className="text-green-600" onClick={() => handleProcessWithdrawal(w.id, 'approve')}><CheckCircle size={14} /></Button><Button size="sm" variant="outline" className="text-red-600" onClick={() => handleProcessWithdrawal(w.id, 'reject')}><XCircle size={14} /></Button></div>}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>
          </TabsContent>

          <TabsContent value="properties" className="space-y-4">
            <Card><CardHeader><CardTitle className="flex items-center gap-2"><Building className="text-emerald-600" />Properties with Commission</CardTitle></CardHeader><CardContent>
              <Table><TableHeader><TableRow><TableHead>Property</TableHead><TableHead>Price</TableHead><TableHead>Type</TableHead><TableHead>Commission</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {properties.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">No properties</TableCell></TableRow> : properties.map(prop => (
                    <TableRow key={prop.id}>
                      <TableCell><p className="font-medium">{prop.property_name}</p><p className="text-xs text-slate-500">{prop.size}</p></TableCell>
                      <TableCell className="font-semibold">₹{(prop.total_price || 0).toLocaleString()}</TableCell>
                      <TableCell>{prop.mlm_enabled ? <Badge className={prop.commission_type === 'fixed' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}>{prop.commission_type === 'fixed' ? 'Fixed' : '%'}</Badge> : '-'}</TableCell>
                      <TableCell>{prop.mlm_enabled ? <span className="font-semibold text-emerald-600">{prop.commission_type === 'fixed' ? '₹' + (prop.commission_value || 0).toLocaleString() : (prop.commission_value || 0) + '%'} <span className="text-xs text-slate-500">(₹{(prop.estimated_commission || 0).toLocaleString()})</span></span> : '-'}</TableCell>
                      <TableCell><Badge className={prop.mlm_enabled ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-800'}>{prop.mlm_enabled ? 'Enabled' : 'Disabled'}</Badge></TableCell>
                      <TableCell><div className="flex gap-2">{prop.mlm_enabled && <Button size="sm" variant="outline" onClick={() => { setSelectedProperty(prop); setCommissionForm({ commission_type: prop.commission_type || 'percentage', commission_value: prop.commission_value || 2, level_distribution: prop.level_distribution || {} }); setPropertyCommissionOpen(true); }} className="text-blue-600"><Edit2 size={14} /></Button>}<Button size="sm" variant={prop.mlm_enabled ? 'outline' : 'default'} onClick={() => handleToggleMLMProperty(prop.id, prop.mlm_enabled)} className={prop.mlm_enabled ? 'text-red-600' : 'bg-emerald-600'}>{prop.mlm_enabled ? 'Disable' : 'Enable'}</Button></div></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>
          </TabsContent>

          <TabsContent value="settings" className="space-y-4">
            {settingsForm && (
              <div className="space-y-6">
                <div className="flex justify-between items-center"><h2 className="text-lg font-semibold flex items-center gap-2"><SettingsIcon className="text-emerald-600" />Commission Settings</h2>{editingSettings ? <div className="flex gap-2"><Button variant="outline" onClick={() => { setEditingSettings(false); setSettingsForm(settings); }}>Cancel</Button><Button className="bg-emerald-600" onClick={handleSaveSettings}><Save size={16} className="mr-2" />Save</Button></div> : <Button variant="outline" onClick={() => setEditingSettings(true)}><Edit2 size={16} className="mr-2" />Edit</Button>}</div>
                <div className="grid md:grid-cols-2 gap-6">
                  <Card><CardHeader><CardTitle>Basic Commission</CardTitle></CardHeader><CardContent className="space-y-4">
                    <div><Label>Direct Sale (%)</Label>{editingSettings ? <Input type="number" step="0.1" value={settingsForm.direct_sale_commission} onChange={e => setSettingsForm({ ...settingsForm, direct_sale_commission: parseFloat(e.target.value) || 0 })} /> : <p className="text-2xl font-bold text-emerald-600">{settings.direct_sale_commission}%</p>}</div>
                    <div><Label>Closing Bonus (₹)</Label>{editingSettings ? <Input type="number" value={settingsForm.closing_bonus} onChange={e => setSettingsForm({ ...settingsForm, closing_bonus: parseFloat(e.target.value) || 0 })} /> : <p className="text-2xl font-bold text-emerald-600">₹{(settings.closing_bonus || 0).toLocaleString()}</p>}</div>
                  </CardContent></Card>
                  <Card><CardHeader><CardTitle>Level Commissions (%)</CardTitle></CardHeader><CardContent><div className="grid grid-cols-5 gap-2">{['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'].map(l => (<div key={l} className="text-center p-2 rounded-lg bg-slate-50"><p className="text-xs text-slate-500 mb-1">L{l}</p>{editingSettings ? <Input type="number" step="0.1" value={settingsForm.level_commissions?.[l] || 0} onChange={e => updateLevelCommission(l, e.target.value)} className="text-center h-8 text-sm" /> : <p className="font-bold text-emerald-600">{settings.level_commissions?.[l] || 0}%</p>}</div>))}</div></CardContent></Card>
                  <Card className="md:col-span-2"><CardHeader><CardTitle>Rank Criteria</CardTitle></CardHeader><CardContent><div className="grid grid-cols-5 gap-4">{['Bronze', 'Silver', 'Gold', 'Platinum', 'Diamond'].map(r => (<div key={r} className="text-center p-4 rounded-xl bg-slate-50"><Badge className={getRankColor(r) + ' mb-2'}>{r}</Badge>{editingSettings ? <Input type="number" value={settingsForm.rank_criteria?.[r] || 0} onChange={e => setSettingsForm({ ...settingsForm, rank_criteria: { ...settingsForm.rank_criteria, [r]: parseInt(e.target.value) || 0 } })} className="text-center mt-2" /> : <p className="text-sm text-slate-600 mt-2">Min: {settings.rank_criteria?.[r] || 0}</p>}</div>))}</div></CardContent></Card>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Add Agent Dialog */}
        <Dialog open={addAgentOpen} onOpenChange={setAddAgentOpen}><DialogContent className="max-w-md"><DialogHeader><DialogTitle>Add Agent</DialogTitle><DialogDescription>Create new MLM agent</DialogDescription></DialogHeader>
          <form onSubmit={handleAddAgent} className="space-y-4"><div><Label>Name *</Label><Input value={agentForm.name} onChange={e => setAgentForm({ ...agentForm, name: e.target.value })} required /></div><div><Label>Email *</Label><Input type="email" value={agentForm.email} onChange={e => setAgentForm({ ...agentForm, email: e.target.value })} required /></div><div><Label>Phone *</Label><Input value={agentForm.phone} onChange={e => setAgentForm({ ...agentForm, phone: e.target.value })} required /></div><div><Label>Password *</Label><Input type="password" value={agentForm.password} onChange={e => setAgentForm({ ...agentForm, password: e.target.value })} required /></div><div><Label>Sponsor Code</Label><Input value={agentForm.sponsor_code} onChange={e => setAgentForm({ ...agentForm, sponsor_code: e.target.value.toUpperCase() })} placeholder="Optional" /></div><Button type="submit" className="w-full bg-emerald-600">Add Agent</Button></form>
        </DialogContent></Dialog>

        {/* View Agent Dialog */}
        <Dialog open={viewAgentOpen} onOpenChange={setViewAgentOpen}><DialogContent className="max-w-lg"><DialogHeader><DialogTitle>Agent Details</DialogTitle><DialogDescription>View agent info</DialogDescription></DialogHeader>
          {selectedAgent && (<div className="space-y-4"><div className="flex items-center gap-4"><div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white text-2xl font-bold">{selectedAgent.name?.charAt(0) || '?'}</div><div><h3 className="text-xl font-bold">{selectedAgent.name}</h3><div className="flex gap-2 mt-1"><Badge className={getRankColor(selectedAgent.rank)}>{selectedAgent.rank}</Badge><Badge className={getStatusColor(selectedAgent.status)}>{selectedAgent.status}</Badge>{selectedAgent.is_inactive && <Badge className="bg-red-100 text-red-800">Inactive</Badge>}</div></div></div><div className="grid grid-cols-2 gap-4"><div><Label className="text-slate-500">Email</Label><p>{selectedAgent.email}</p></div><div><Label className="text-slate-500">Phone</Label><p>{selectedAgent.phone}</p></div><div><Label className="text-slate-500">Referral Code</Label><code className="bg-slate-100 px-2 py-1 rounded">{selectedAgent.referral_code}</code></div><div><Label className="text-slate-500">Last Login</Label><p className={selectedAgent.is_inactive ? 'text-red-600 font-semibold' : ''}>{formatLastLogin(selectedAgent.last_login, selectedAgent.days_since_login)}</p></div><div><Label className="text-slate-500">Joined</Label><p>{new Date(selectedAgent.created_at).toLocaleDateString()}</p></div><div><Label className="text-slate-500">Days Since Login</Label><p>{selectedAgent.days_since_login !== null ? selectedAgent.days_since_login + ' days' : 'Never logged in'}</p></div></div>
          <div className="flex gap-2 pt-4 border-t"><Button variant="outline" className="flex-1" onClick={() => { setViewAgentOpen(false); openEditDialog(selectedAgent); }}><Edit2 size={16} className="mr-2" />Edit</Button><Button variant="outline" className="flex-1" onClick={() => { setViewAgentOpen(false); openResetPasswordDialog(selectedAgent); }}><Key size={16} className="mr-2" />Reset Password</Button></div>
          </div>)}
        </DialogContent></Dialog>

        {/* Edit Agent Dialog */}
        <Dialog open={editAgentOpen} onOpenChange={setEditAgentOpen}><DialogContent className="max-w-md"><DialogHeader><DialogTitle>Edit Agent</DialogTitle><DialogDescription>Update agent details</DialogDescription></DialogHeader>
          <form onSubmit={handleEditAgent} className="space-y-4">
            <div><Label>Name *</Label><Input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} required /></div>
            <div><Label>Email *</Label><Input type="email" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} required /></div>
            <div><Label>Phone *</Label><Input value={editForm.phone} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} required /></div>
            <div><Label>Status</Label>
              <Select value={editForm.status} onValueChange={v => setEditForm({ ...editForm, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="pending">Pending</SelectItem><SelectItem value="suspended">Suspended</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Rank</Label>
              <Select value={editForm.rank} onValueChange={v => setEditForm({ ...editForm, rank: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Bronze">Bronze</SelectItem><SelectItem value="Silver">Silver</SelectItem><SelectItem value="Gold">Gold</SelectItem><SelectItem value="Platinum">Platinum</SelectItem><SelectItem value="Diamond">Diamond</SelectItem></SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full bg-emerald-600">Save Changes</Button>
          </form>
        </DialogContent></Dialog>

        {/* Reset Password Dialog */}
        <Dialog open={resetPasswordOpen} onOpenChange={setResetPasswordOpen}><DialogContent className="max-w-md"><DialogHeader><DialogTitle>Reset Password</DialogTitle><DialogDescription>Set new password for {selectedAgent?.name}</DialogDescription></DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div><Label>New Password *</Label><Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Minimum 6 characters" required minLength={6} /></div>
            <p className="text-sm text-slate-500">Agent will need to use this new password to login.</p>
            <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700"><Key size={16} className="mr-2" />Reset Password</Button>
          </form>
        </DialogContent></Dialog>

        {/* Property Commission Dialog */}
        <Dialog open={propertyCommissionOpen} onOpenChange={setPropertyCommissionOpen}><DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle className="flex items-center gap-2"><IndianRupee className="text-emerald-600" />Commission - {selectedProperty?.property_name}</DialogTitle><DialogDescription>Configure commission</DialogDescription></DialogHeader>
          {selectedProperty && (<div className="space-y-6">
            <div className="bg-slate-50 p-4 rounded-xl grid grid-cols-2 gap-4"><div><p className="text-sm text-slate-500">Property</p><p className="font-semibold">{selectedProperty.property_name}</p></div><div><p className="text-sm text-slate-500">Price</p><p className="font-bold text-emerald-600">₹{(selectedProperty.total_price || 0).toLocaleString()}</p></div></div>
            <div className="space-y-3"><Label className="text-base font-semibold">Type</Label><div className="grid grid-cols-2 gap-4"><button type="button" onClick={() => setCommissionForm({ ...commissionForm, commission_type: 'percentage' })} className={'p-4 rounded-xl border-2 ' + (commissionForm.commission_type === 'percentage' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200')}><Percent className={'mx-auto mb-2 ' + (commissionForm.commission_type === 'percentage' ? 'text-emerald-600' : 'text-slate-400')} /><p className="font-semibold">Percentage</p></button><button type="button" onClick={() => setCommissionForm({ ...commissionForm, commission_type: 'fixed' })} className={'p-4 rounded-xl border-2 ' + (commissionForm.commission_type === 'fixed' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200')}><IndianRupee className={'mx-auto mb-2 ' + (commissionForm.commission_type === 'fixed' ? 'text-emerald-600' : 'text-slate-400')} /><p className="font-semibold">Fixed</p></button></div></div>
            <div><Label>{commissionForm.commission_type === 'percentage' ? 'Percentage (%)' : 'Amount (₹)'}</Label><Input type="number" step={commissionForm.commission_type === 'percentage' ? '0.1' : '1000'} value={commissionForm.commission_value} onChange={e => setCommissionForm({ ...commissionForm, commission_value: parseFloat(e.target.value) || 0 })} className="text-lg font-semibold" /><p className="text-sm text-emerald-600 mt-1">Total: ₹{(commissionForm.commission_type === 'percentage' ? (selectedProperty.total_price * commissionForm.commission_value / 100) : commissionForm.commission_value).toLocaleString()}</p></div>
            <div><div className="flex justify-between"><Label>Level Distribution (%)</Label><span className={'text-sm font-medium ' + (Math.abs(Object.values(commissionForm.level_distribution).reduce((a, b) => Number(a) + Number(b), 0) - 100) < 0.01 ? 'text-emerald-600' : 'text-red-600')}>Total: {Object.values(commissionForm.level_distribution).reduce((a, b) => Number(a) + Number(b), 0).toFixed(1)}%</span></div><div className="grid grid-cols-5 gap-3 mt-2">{['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'].map(l => { const tc = commissionForm.commission_type === 'percentage' ? (selectedProperty.total_price * commissionForm.commission_value / 100) : commissionForm.commission_value; const la = (tc * (commissionForm.level_distribution[l] || 0)) / 100; return (<div key={l} className="p-2 rounded-xl bg-slate-50 text-center"><p className="text-xs text-slate-500">L{l}</p><Input type="number" step="0.5" min="0" value={commissionForm.level_distribution[l] || 0} onChange={e => updateDistribution(l, e.target.value)} className="text-center h-8 mb-1" /><p className="text-xs font-semibold text-emerald-600">₹{la.toLocaleString()}</p></div>); })}</div></div>
            <div className="flex flex-wrap gap-2"><Button type="button" variant="outline" size="sm" onClick={() => setCommissionForm({ ...commissionForm, level_distribution: { "1": 40, "2": 20, "3": 15, "4": 10, "5": 5, "6": 3, "7": 3, "8": 2, "9": 1, "10": 1 } })}>Standard</Button><Button type="button" variant="outline" size="sm" onClick={() => setCommissionForm({ ...commissionForm, level_distribution: { "1": 50, "2": 25, "3": 15, "4": 5, "5": 3, "6": 1, "7": 0.5, "8": 0.3, "9": 0.1, "10": 0.1 } })}>Top Heavy</Button><Button type="button" variant="outline" size="sm" onClick={() => setCommissionForm({ ...commissionForm, level_distribution: { "1": 10, "2": 10, "3": 10, "4": 10, "5": 10, "6": 10, "7": 10, "8": 10, "9": 10, "10": 10 } })}>Equal</Button></div>
            <div className="flex gap-3 pt-4"><Button variant="outline" className="flex-1" onClick={() => setPropertyCommissionOpen(false)}>Cancel</Button><Button className="flex-1 bg-emerald-600" onClick={handleSavePropertyCommission}><Save size={16} className="mr-2" />Save</Button></div>
          </div>)}
        </DialogContent></Dialog>

        {/* Add Payment Dialog */}
        <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><IndianRupee className="text-purple-600" />Add Payment</DialogTitle>
              <DialogDescription>Record payment for {selectedBooking?.customer_name}</DialogDescription>
            </DialogHeader>
            {selectedBooking && (
              <div className="space-y-4">
                <div className="bg-slate-50 p-4 rounded-xl grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-500">Property</p>
                    <p className="font-semibold text-sm">{selectedBooking.property_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Total Amount</p>
                    <p className="font-bold text-emerald-600">₹{(selectedBooking.total_amount || 0).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Already Paid</p>
                    <p className="font-semibold text-green-600">₹{(selectedBooking.paid_amount || 0).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Balance</p>
                    <p className="font-semibold text-red-600">₹{(selectedBooking.balance_amount || 0).toLocaleString()}</p>
                  </div>
                </div>
                
                <div>
                  <Label>Payment Amount (₹) *</Label>
                  <Input 
                    type="number" 
                    placeholder="Enter amount" 
                    value={paymentForm.amount} 
                    onChange={e => setPaymentForm({...paymentForm, amount: e.target.value})}
                    className="text-lg font-semibold"
                  />
                </div>
                
                <div>
                  <Label>Payment Mode *</Label>
                  <Select value={paymentForm.payment_mode} onValueChange={v => setPaymentForm({...paymentForm, payment_mode: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Cash">Cash</SelectItem>
                      <SelectItem value="UPI">UPI</SelectItem>
                      <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                      <SelectItem value="Cheque">Cheque</SelectItem>
                      <SelectItem value="DD">Demand Draft</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Remarks (Optional)</Label>
                  <Input 
                    placeholder="e.g., Cheque #12345, UTR: ABC123" 
                    value={paymentForm.remarks} 
                    onChange={e => setPaymentForm({...paymentForm, remarks: e.target.value})}
                  />
                </div>
                
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" className="flex-1" onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
                  <Button className="flex-1 bg-purple-600 hover:bg-purple-700" onClick={handleSubmitPayment}>
                    <IndianRupee size={16} className="mr-2" />Record Payment
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Complete Registry Dialog */}
        <Dialog open={registryDialogOpen} onOpenChange={setRegistryDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><FileText className="text-blue-600" />Complete Registry</DialogTitle>
              <DialogDescription>Mark registry as complete for {selectedBooking?.customer_name}</DialogDescription>
            </DialogHeader>
            {selectedBooking && (
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-xl">
                  <p className="text-sm text-blue-800 font-medium">Property: {selectedBooking.property_name}</p>
                  <p className="text-sm text-blue-600">Customer: {selectedBooking.customer_name}</p>
                  <p className="text-sm text-blue-600">Agent: {selectedBooking.agent_name}</p>
                </div>
                
                <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
                  <p className="text-sm text-amber-800 font-medium flex items-center gap-2">
                    <CheckCircle size={16} />
                    Commission will be released to agents upon completion
                  </p>
                  <p className="text-xs text-amber-600 mt-1">
                    Total Commission: ₹{(selectedBooking.total_commission || 0).toLocaleString()}
                  </p>
                </div>
                
                <div>
                  <Label>Registry Number *</Label>
                  <Input 
                    placeholder="e.g., REG-2026-001234" 
                    value={registryForm.registry_number} 
                    onChange={e => setRegistryForm({...registryForm, registry_number: e.target.value})}
                    className="font-mono"
                  />
                </div>
                
                <div>
                  <Label>Registry Date *</Label>
                  <Input 
                    type="date" 
                    value={registryForm.registry_date} 
                    onChange={e => setRegistryForm({...registryForm, registry_date: e.target.value})}
                  />
                </div>
                
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" className="flex-1" onClick={() => setRegistryDialogOpen(false)}>Cancel</Button>
                  <Button className="flex-1 bg-blue-600 hover:bg-blue-700" onClick={handleSubmitRegistry}>
                    <CheckCircle size={16} className="mr-2" />Complete Registry
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
